import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ResourceLibrary = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', label: 'All Resources', icon: 'Library', count: 24 },
    { id: 'anxiety', label: 'Anxiety', icon: 'Brain', count: 8 },
    { id: 'depression', label: 'Depression', icon: 'Cloud', count: 6 },
    { id: 'stress', label: 'Stress Management', icon: 'Activity', count: 5 },
    { id: 'sleep', label: 'Sleep', icon: 'Moon', count: 3 },
    { id: 'relationships', label: 'Relationships', icon: 'Users', count: 2 }
  ];

  const resources = [
    {
      id: 1,
      title: "Understanding Anxiety: A Comprehensive Guide",
      description: "Learn about anxiety symptoms, triggers, and evidence-based coping strategies to manage daily challenges.",
      category: "anxiety",
      type: "article",
      duration: "10 min read",
      icon: "FileText",
      featured: true
    },
    {
      id: 2,
      title: "Mindfulness Meditation for Beginners",
      description: "Step-by-step guided meditation practices to reduce stress and improve emotional wellbeing.",
      category: "stress",
      type: "video",
      duration: "15 min",
      icon: "Video",
      featured: true
    },
    {
      id: 3,
      title: "Sleep Hygiene Checklist",
      description: "Practical tips and habits to improve sleep quality and establish healthy sleep patterns.",
      category: "sleep",
      type: "checklist",
      duration: "5 min read",
      icon: "CheckSquare",
      featured: false
    },
    {
      id: 4,
      title: "Cognitive Behavioral Therapy Techniques",
      description: "Self-help CBT exercises to challenge negative thoughts and develop healthier thinking patterns.",
      category: "depression",
      type: "worksheet",
      duration: "20 min",
      icon: "Edit",
      featured: false
    },
    {
      id: 5,
      title: "Building Healthy Relationships",
      description: "Communication strategies and boundary-setting techniques for stronger personal connections.",
      category: "relationships",
      type: "article",
      duration: "12 min read",
      icon: "FileText",
      featured: false
    },
    {
      id: 6,
      title: "Breathing Exercises for Anxiety",
      description: "Quick and effective breathing techniques to calm your nervous system during anxious moments.",
      category: "anxiety",
      type: "audio",
      duration: "8 min",
      icon: "Headphones",
      featured: true
    }
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'video':
        return 'bg-error/10 text-error';
      case 'audio':
        return 'bg-warning/10 text-warning';
      case 'worksheet':
        return 'bg-success/10 text-success';
      default:
        return 'bg-primary/10 text-primary';
    }
  };

  const filteredResources = resources?.filter(resource =>
    resource?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    resource?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleViewResource = (resourceId) => {
    // Navigate to resource detail page or open external link
    console.log('Viewing resource:', resourceId);
    // For now, just log - in production this would navigate to resource detail
  };

  const handleOpenResource = (resourceId) => {
    // Open resource in new tab or modal
    console.log('Opening resource:', resourceId);
    // For now, just log - in production this would open resource
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground mb-2">Resource Library</h2>
        <p className="text-muted-foreground">Curated mental health information and self-help tools</p>
      </div>
      {/* Search */}
      <Input
        type="search"
        placeholder="Search resources..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e?.target?.value)}
        className="w-full"
      />
      {/* Categories */}
      <div className="flex flex-wrap gap-2">
        {categories?.map((category) => (
          <button
            key={category?.id}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all duration-200"
          >
            <Icon name={category?.icon} size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">{category?.label}</span>
            <span className="text-xs text-muted-foreground">({category?.count})</span>
          </button>
        ))}
      </div>
      {/* Featured Resources */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Featured Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredResources?.filter(r => r?.featured)?.map((resource) => (
            <div key={resource?.id} className="card-elevated p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={resource?.icon} size={24} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="text-base font-semibold text-foreground">{resource?.title}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(resource?.type)}`}>
                      {resource?.type}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{resource?.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      <span>{resource?.duration}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="ArrowRight" 
                      iconPosition="right"
                      onClick={() => handleViewResource(resource?.id)}>
                      View
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* All Resources */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">All Resources</h3>
        <div className="space-y-3">
          {filteredResources?.filter(r => !r?.featured)?.map((resource) => (
            <div key={resource?.id} className="card-elevated p-4 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <Icon name={resource?.icon} size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-foreground">{resource?.title}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(resource?.type)}`}>
                      {resource?.type}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{resource?.description}</p>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{resource?.duration}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    iconName="ExternalLink"
                    onClick={() => handleOpenResource(resource?.id)}>
                    Open
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {filteredResources?.length === 0 && (
        <div className="card-elevated p-8 text-center">
          <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Resources Found</h3>
          <p className="text-muted-foreground">Try adjusting your search or browse by category</p>
        </div>
      )}
    </div>
  );
};

export default ResourceLibrary;